/*
 * This component has 2 primary responsibilities
 * 1)  a successful login into Azure
 * If it is an existing user, route to the dashboard or whatever private route they were trying to access before
 * being redirected to the login screen.
 * If it is a new user, route to the signup page
 * 2) provide a plane for the user to begin logging in
 */

import * as React from 'react';

import { Button, Col, Container, Row } from 'react-bootstrap';
import { TFunction, WithTranslation, withTranslation } from 'react-i18next';
import { Iuser } from '../../models';
import { MSALlogin, getHoursCheck, resetUserLogout, userLogin } from '../../actions/userActions';
import { debugMSAL } from './Auth-Utils';
import { initialSyncStart, setUpInitialAppSync } from '../../actions/commonActions';

import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import msalInstance from './MsalInstance';
import { IinitialState } from '../../reducers';
import { selectIsLoading } from '../../reducers/commonReducers';
import Mixpanel from '../../helpers/Mixpanel';
import { langOption } from '../../i18n';

import azure from '../../images/Azure.png';

const Loading = () => <h3>Checking Authentication...</h3>;

interface Iprops extends RouteComponentProps<Record<string, never>> {
    MSALlogin: (t: TFunction) => Promise<any>;
    userLogin: (t: TFunction) => any;
    user: Iuser;
    t: TFunction;
    loading: boolean;
    online: boolean;
    getHoursCheck: typeof getHoursCheck;
    setUpInitialAppSync: typeof setUpInitialAppSync;
    initialSyncStart: typeof initialSyncStart;
    resetUserLogout: typeof resetUserLogout;
}

interface Istate {
    userIsAuthenticated: boolean;
    userLoginFailed: boolean;
    isProcessing: boolean;
}

class Login extends React.Component<Iprops & WithTranslation, Istate> {
    constructor(props: Iprops & WithTranslation) {
        super(props);
        this.state = {
            userIsAuthenticated: false,
            userLoginFailed: false,
            isProcessing: false
        };
    }

    componentDidMount() {
        document.addEventListener('missingUser', this.handleRedirectToSignup, false);
        const locationState: any = this.props.location.state;
        const { from } = locationState || { from: { pathname: '/jobs' } };

        this.initializeAuth(from);
    }

    componentWillUnmount() {
        document.removeEventListener('missingUser', this.handleRedirectToSignup, false);
    }

    componentDidUpdate(prevProps: Readonly<this['props']>) {
        // Only run when user becomes authenticated
        if (!prevProps.user.isAuthenticated && this.props.user.isAuthenticated) {
            const locationState = this.props.location.state as { from?: { pathname: string } } | undefined;
            const from = locationState?.from || { pathname: '/jobs' };
            this.handleSuccessfulAuth(from);
            this.props.setUpInitialAppSync();
            this.props.initialSyncStart();
            this.props.resetUserLogout();
            this.props.getHoursCheck();
        }
    }

    initializeAuth = async (from: any) => {
        // If user is already authenticated, bypass the isProcessing check and redirect immediately
        if (this.props.user.isAuthenticated) {
            this.handleSuccessfulAuth(from);
            return;
        }

        if (this.state.isProcessing) {
            console.log('[initializeAuth]: Already processing, skipping');
            return;
        }

        try {
            // First, handle any redirect promise that might be pending
            const redirectResponse = await msalInstance.msalApp.handleRedirectPromise();
            if (redirectResponse) {
                console.log('[initializeAuth]: Found redirect response, proceeding with login');
                // Don't set isProcessing here, let handleLogin manage its own state
                await this.handleLogin(from);
                return;
            }

            const accounts = msalInstance.msalApp.getAllAccounts();
            debugMSAL('msal accounts: ' + accounts.length);

            if (accounts.length > 0) {
                if (!this.props.user.isAuthenticated) {
                    // Don't set isProcessing here, let handleLogin manage its own state
                    await this.handleLogin(from);
                } else {
                    debugMSAL('user is fully authenticated and the login component mounted');
                    this.handleSuccessfulAuth(from);
                }
            } else {
                debugMSAL('no msal account');
                if (this.props.user.isAuthenticated) {
                    if (this.props.online === false) {
                        this.handleSuccessfulAuth(from);
                    } else {
                        // Don't set isProcessing here, let handleLogin manage its own state
                        await this.handleLogin(from);
                    }
                }
            }
        } catch (error) {
            console.error('[initializeAuth]:', error);
            this.setState({ userLoginFailed: true });
        }
    };

    handleSuccessfulAuth = (from: any) => {
        if (this.props.user.language) {
            const lang = langOption(this.props.user.language);
            this.props.i18n.changeLanguage(lang);
        }

        this.setState({ userIsAuthenticated: true });

        // Always redirect to jobs if we're on the login page
        if (this.props.location.pathname === '/') {
            console.log('[handleSuccessfulAuth]: Redirecting to jobs');
            this.props.history.push('/jobs');
        } else {
            // Otherwise redirect to the original destination
            console.log('[handleSuccessfulAuth]: Redirecting to original destination:', from.pathname);
            this.props.history.push(from.pathname);
        }
    };

    handleLogin = async (from: any) => {
        console.log('[handleLogin]: Starting login process');
        console.log('[handleLogin]: isProcessing:', this.state.isProcessing);

        // If we're coming from a redirect, we need to proceed even if isProcessing is true
        const isFromRedirect = this.props.location.pathname === '/' && this.props.location.search.includes('code=');

        if (this.state.isProcessing && !isFromRedirect) {
            console.log('[handleLogin]: Already processing, skipping');
            return;
        }

        this.setState({ isProcessing: true });

        try {
            debugMSAL('loading with MSAL account and logging user into app.');
            await this.props.userLogin(this.props.t);

            Mixpanel.track('Login Successful');
            debugMSAL('logged user in successfully');

            // Check if user is authenticated after the API call
            if (this.props.user.isAuthenticated) {
                console.log('[handleLogin]: User is authenticated, proceeding with successful auth');
                this.handleSuccessfulAuth(from);
                this.props.setUpInitialAppSync();
                this.props.initialSyncStart();
                this.props.resetUserLogout();
                this.props.getHoursCheck();
            } else {
                this.setState({ userLoginFailed: true });
            }
        } catch (error) {
            console.error('[handleLogin]:', error);
            Mixpanel.track('Login Unsuccessful');
            this.setState({ userLoginFailed: true });

            // If the error is due to token expiration or interaction required, try to re-authenticate
            if (
                error &&
                typeof error === 'object' &&
                'errorCode' in error &&
                typeof error.errorCode === 'string' &&
                (error.errorCode.includes('token_expired') ||
                    error.errorCode.includes('interaction_required') ||
                    error.errorCode.includes('interaction_in_progress'))
            ) {
                await this.handleMsalLogin();
            }
        } finally {
            this.setState({ isProcessing: false });
        }
    };

    handleMsalLogin = async () => {
        if (this.state.isProcessing) {
            return;
        }

        this.setState({ isProcessing: true });

        try {
            // First, handle any redirect promise that might be pending
            const redirectResponse = await msalInstance.msalApp.handleRedirectPromise();
            if (redirectResponse) {
                console.log('[handleMsalLogin]: Found redirect response, proceeding with login');
                await this.props.MSALlogin(this.props.t);

                // Wait for authentication state to update
                if (this.props.user.isAuthenticated) {
                    const locationState: any = this.props.location.state;
                    const { from } = locationState || { from: { pathname: '/jobs' } };

                    this.props.setUpInitialAppSync();
                    this.props.initialSyncStart();
                    this.props.resetUserLogout();
                    this.props.getHoursCheck();

                    this.handleSuccessfulAuth(from);
                }
                return;
            }

            const accounts = msalInstance.msalApp.getAllAccounts();
            if (accounts.length === 0) {
                console.log('[handleMsalLogin]: No accounts found, initiating login redirect');
                await msalInstance.msalApp.loginRedirect();
                return;
            }

            // Set the first account as active
            const account = accounts[0];
            if (account) {
                msalInstance.msalApp.setActiveAccount(account);
            }

            // Wait a moment to ensure the account is properly initialized
            await new Promise(resolve => setTimeout(resolve, 500));

            await this.props.MSALlogin(this.props.t);
            Mixpanel.track('Login Successful');
            debugMSAL('logged user in successfully');

            // Wait for authentication state to update
            if (this.props.user.isAuthenticated) {
                const locationState: any = this.props.location.state;
                const { from } = locationState || { from: { pathname: '/jobs' } };

                this.props.setUpInitialAppSync();
                this.props.initialSyncStart();
                this.props.resetUserLogout();
                this.props.getHoursCheck();

                this.handleSuccessfulAuth(from);
            }
        } catch (error) {
            console.error('[handleMsalLogin]:', error);
            this.setState({ userLoginFailed: true });

            // If interaction is in progress, wait and retry
            if (
                error &&
                typeof error === 'object' &&
                'errorCode' in error &&
                typeof error.errorCode === 'string' &&
                error.errorCode.includes('interaction_in_progress')
            ) {
                console.log('[handleMsalLogin]: Interaction in progress, waiting and retrying');
                await new Promise(resolve => setTimeout(resolve, 1000));
                await this.handleMsalLogin();
            }
        } finally {
            this.setState({ isProcessing: false });
        }
    };

    handleRedirectToSignup = () => {
        this.props.history.push('/social_signup');
    };

    render() {
        const { t, user, online, loading } = this.props;

        if (online === false && (user.isAuthenticated || user.isLoggingOut)) {
            Mixpanel.identify(user.id);
            Mixpanel.people.set({
                $email: user.email,
                USER_ID: user.id
            });

            return <Loading />;
        }

        const welcomeMobileText = t('welcomeMobile');
        const loginButtonText = t('loginButton');

        return (
            <div className="loginlayout">
                <Container>
                    <Row>
                        <Col>
                            <div className="loginForm login">
                                <span className="loginTitle">{welcomeMobileText}</span>
                                <Button
                                    variant="default"
                                    className="loginBtn"
                                    onClick={this.handleMsalLogin}
                                    disabled={loading || this.state.isProcessing}
                                >
                                    <img width="20" height="20" src={azure} alt="azure" />
                                    {loginButtonText}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }
}

const mapStateToProps = (state: IinitialState) => ({
    user: state.user,
    loading: selectIsLoading(state),
    online: state.offline.online
});

export default withTranslation('auth')(
    connect(mapStateToProps, {
        userLogin,
        MSALlogin,
        getHoursCheck,
        setUpInitialAppSync,
        initialSyncStart,
        resetUserLogout
    })(Login)
);
